import { AlertTriangle, Info } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { focusValidationError } from '../../../../lib/validationFocus';
import { inventoryItemsApi, storageFacilitiesApi } from '../inventoryApi';
import type { InventoryItemDetail, StorageFacilityOption } from '../inventoryTypes';
import { stockBatchesApi } from '../stockApi';
import {
  ADJUSTMENT_DIRECTION,
  ADJUSTMENT_DIRECTION_DESCRIPTION,
  ADJUSTMENT_DIRECTION_LABEL,
  ADJUSTMENT_REASON_SUGGESTIONS,
  BATCH_STATUS,
  BATCH_STATUS_LABEL,
  applyDirection,
} from '../stockTypes';
import type { StockAdjustmentDirection, StockBatchListItem } from '../stockTypes';
import { sampleAdjustment } from '../utils/sampleStock';

/**
 * What an adjustment is recorded AGAINST.
 *
 * Deliberately the narrowest set of fields the form reads, rather than a stock level: a level
 * row satisfies it structurally, and so does an item that was registered five seconds ago and
 * has no level row yet. That second case is what the registration form's "set the opening count
 * after saving" hands over, and it is the reason this is a shape rather than a type name.
 *
 * `onHand` is 0 for a fresh registration, which is not a placeholder — it is what is true, and
 * the form states it as a read-only fact beside the item.
 */
export interface StockAdjustmentTarget {
  id: string;
  sku: string;
  name: string;
  unitCode: string;
  inventoryCategoryName: string;
  onHand: number;
}

/**
 * The states a lot can be in that an adjustment NEVER corrects.
 *
 * Somebody parked these by hand, so counting one upward would quietly release stock they
 * withheld. The server opens a fresh issuable lot beside it instead — this list is the client's
 * mirror of that rule, and the two must not drift.
 *
 * `depleted` is deliberately absent: a lot that reached zero and is counted again is Active
 * again, which is the ledger's own derivation. So is `expired` — it is DERIVED from the date on every
 * read and the stored status underneath it is still Active, so the server resolves onto it.
 */
const HELD: number[] = [BATCH_STATUS.quarantined, BATCH_STATUS.damaged];

/**
 * Correct what a lot is recorded as holding — how much goes on it, or comes off it.
 *
 * **It REPLACED the restock form on 21 Sep 2026, and the difference is the MOVEMENT it writes.**
 * A restock received a delivery — a `Receipt`, which added to on hand and to the running total
 * of what had ever arrived. This says the RECORD was wrong: a signed `Adjustment`, with
 * `quantityReceived` left alone because nothing arrived.
 *
 * <b>That distinction is the whole module, and this file spent a day contradicting it.</b> The
 * behaviour was converted and the PROSE was not — every doc comment, two visible field
 * descriptions and the generator's own name still said "delivery", "restock" and "top-up" over a
 * form that records a count. A form whose words state the opposite of what it writes is where
 * the reader's mental model comes from, and it is what produced the defects found by USING this
 * screen rather than by reading it. Do not reintroduce any of those three words here.
 *
 * **The item is not asked for.** The form opens from a level row's kebab — or straight off a
 * registration the reader ticked "set the opening count" on — so the item was already chosen by
 * getting here: the locked-parent rule, and the reason it is stated as a read-only fact rather
 * than offered as a picker that can only hold one value.
 *
 * **It takes a {@link StockAdjustmentTarget}, not a stock LEVEL.** A level row satisfies that
 * shape structurally, and so does a freshly registered item with nothing on hand yet — which is
 * what lets the registration form hand straight over without a level row existing for it to read.
 *
 * **It is NOT `<StockBatchModal>` with fields hidden.** That form RECEIVES a new lot and asks
 * which item; this one corrects whatever lot the number resolves to. A shared shape would make
 * the reader work out which of the two is about to happen, which is exactly the question the
 * correction callout below exists to answer for them.
 *
 * **The baseline is the LOT, never the item**, and both figures are on screen for that reason —
 * see the header table. Showing only the item's total left the form judging a correction against
 * a number it was not displaying, which is where "why can I not record a decrease" comes from.
 *
 * **The LOT IS PICKED, not recalled**, and that is what unblocked the case this form was
 * rebuilt for: a duplicate entry added 30 items, an audit found them, and the lot carrying the
 * phantom stock could no longer be identified — so the reader typed a number from memory,
 * nothing resolved, and a legitimate correction was refused. The picker lists the lots the
 * facility actually holds with what each has on hand, and takes a typed number only on an ADD,
 * where opening a new lot is a real answer.
 *
 * Two divergences from the reference, both recorded on the server DTO: the **batch number is
 * required**, because this product does not invent a lot number for a lot it did not label; and
 * the **cost is required too**, from 21 Sep 2026, reversing the call this form shipped with.
 *
 * <b>The cost is only READ where the adjustment OPENS a lot</b>, exactly as the expiry date is —
 * correcting a lot that already exists does not restate what it cost. Both fields say so in
 * their own descriptions and the header states the lot's recorded cost beside them. Requiring a
 * figure while implying it is recorded is the failure that pairing exists to prevent, and it
 * shipped that way for an hour.
 */
export function StockAdjustmentModal({
  open,
  target,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** What the adjustment is against. Null closes the form. */
  target: StockAdjustmentTarget | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [facilityId, setFacilityId] = useState('');
  const [batchNumber, setBatchNumber] = useState('');

  /*
    THE CHANGE, not a total — reversed on 21 Sep 2026. This field used to ask what was
    physically COUNTED and let the server subtract; a reader correcting an entry added in error
    knows the change and not the total, entered 30 meaning "take 30 off", and the form recorded
    it as a shelf holding 30.

    UNSIGNED. The direction beside it carries the sign, so a dropped minus can never turn a
    removal into an addition — see `ADJUSTMENT_DIRECTION`.
  */
  const [quantity, setQuantity] = useState('');

  /*
    SEEDED EMPTY, and that is the required-yes/no rule rather than an oversight: Add and Deduct
    are opposite claims about the same number, so a pre-selected side records an answer nobody
    gave while looking answered. The primary action stays dead until it is chosen, and says so.
  */
  const [direction, setDirection] = useState<string>('');

  /*
    FREE TEXT with suggestions, from 21 Sep 2026 — it carried an enum until the direction took
    over the sign. Seeded with the commonest of the four rather than empty: it is required, and
    a picker that opens blank on the one field nothing can validate is a field the reader has to
    answer before they have decided anything.
  */
  const [reason, setReason] = useState<string>(ADJUSTMENT_REASON_SUGGESTIONS[0]!);
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');

  const [item, setItem] = useState<InventoryItemDetail | null>(null);
  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);
  const [existing, setExisting] = useState<StockBatchListItem[] | null>(null);
  const [loading, setLoading] = useState(false);

  /*
    WHICH lot the correction was acknowledged for — an id, never a bare boolean.

    Storing the target is what ties the acknowledgement to the thing acknowledged: changing the
    batch number or the facility moves the resolution onto a different lot, and a boolean would
    carry the tick across onto one the reader never agreed to.
  */
  const [ackedLotId, setAckedLotId] = useState<string | null>(null);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const itemId = target?.id ?? null;

  useEffect(() => {
    if (!open || !itemId) return;

    setFacilityId('');
    setBatchNumber('');
    setQuantity('');
    setDirection('');
    setReason(ADJUSTMENT_REASON_SUGGESTIONS[0]!);
    setExpiryDate('');
    setNotes('');
    setItem(null);
    setExisting(null);
    setAckedLotId(null);
    setConfirmed(false);
    setError(null);

    const controller = new AbortController();
    setLoading(true);

    Promise.all([
      inventoryItemsApi.get(itemId, controller.signal),
      storageFacilitiesApi.options(controller.signal),

      /*
        The lots this item already has, which is what the correction callout reads. ACTIVE only,
        because the server's own resolve requires `IsActive` — a deleted lot is skipped there,
        so a callout that counted one would promise a correction that does not happen.
      */
      stockBatchesApi.list({ item: itemId, status: 'active', pageSize: 200 }, controller.signal),
    ])
      .then(([loadedItem, loadedFacilities, lots]) => {
        if (controller.signal.aborted) return;

        setItem(loadedItem);
        setFacilities(loadedFacilities);
        setExisting(lots.items);

        /*
          NOTHING SEEDS THE COST any more. It used to come from the catalog's reference price,
          which was dropped on 20 Sep 2026 — see the comment where `InventoryItem.UnitCost` was.

          Stated rather than left to be rediscovered: a suggestion is still worth having, and the
          honest source for one is the LAST LOT RECEIVED for this item, which `existing` above is
          already holding. That is a deliberate gap, not a reason to put a price back on the
          catalog row.
        */
      })
      // A failed feed names no field, so it must not reach the save path's error state. The
      // callout simply does not render, and the server still resolves correctly.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, itemId]);

  const tracksExpiry = item?.tracksExpiry ?? false;
  const typed = batchNumber.trim().toLowerCase();

  /*
    The client's MIRROR of the server's resolution, so the callout promises what will actually
    happen. It keys on item + facility + batch number and skips a lot somebody parked.

    The comparison is case-INSENSITIVE because SQL Server's default collation is, so `lot-a`
    resolves onto `LOT-A` there. Matching case-sensitively here would leave the form promising a
    new lot and the server topping one up.
  */
  const { existingLot, held, elsewhere } = useMemo(() => {
    const none = {
      existingLot: null as StockBatchListItem | null,
      held: [] as StockBatchListItem[],
      elsewhere: [] as StockBatchListItem[],
    };

    if (!existing || !typed) return none;

    const sameNumber = existing.filter((b) => b.batchNumber.trim().toLowerCase() === typed);
    if (sameNumber.length === 0) return none;

    const here = sameNumber.filter((b) => b.storageFacilityId === facilityId);

    return {
      existingLot: here.find((b) => !HELD.includes(b.status)) ?? null,
      held: here.filter((b) => HELD.includes(b.status)),
      elsewhere: sameNumber.filter((b) => b.storageFacilityId !== facilityId),
    };
  }, [existing, typed, facilityId]);

  const quantityNumber = Number(quantity);

  // NOT `>= 0`: zero adjusts nothing, which the validator refuses from the request alone.
  const quantityGiven =
    quantity.trim() !== '' && Number.isInteger(quantityNumber) && quantityNumber > 0;

  const chosenDirection =
    direction === '' ? null : (Number(direction) as StockAdjustmentDirection);

  /*
    THE CORRECTION, mirrored from the server so every callout promises what will happen.

    `recorded` is what the LOT holds, and a batch number naming no lot makes it zero — which is
    what opens a lot on an Add and what refuses every Deduct. Both halves of that are stated on
    screen rather than left for the reader to infer; see the header's `This lot` row.
  */
  const recorded = existingLot?.quantityOnHand ?? 0;
  const delta =
    quantityGiven && chosenDirection !== null ? applyDirection(chosenDirection, quantityNumber) : 0;

  /*
    NOTHING CHECKS THE REASON, and the absence is what to read here rather than the code that is
    gone. A mirror of `StockAdjustmentReasons.Allows` lived at this line and refused a reason
    that contradicted the sign; the reason is an arbitrary string now, so there is nothing to
    check it against. The DIRECTION decides which way the quantity moves, on its own.
  */

  /*
    THE LOTS THIS FACILITY ACTUALLY HOLDS — what the batch number picker offers.

    <b>It exists because "which lot" is a question a reader often cannot answer from memory</b>,
    and the form used to make them type it. The case that forced it: a duplicate entry added 30
    items, a physical audit found them, and the lot carrying the phantom stock could no longer
    be identified — so the reader guessed a number, nothing resolved, and the form refused a
    correction that was entirely legitimate.

    Offering what is there is the standing rule — "a control that lists values the server will
    refuse is a control that produces rejections" — and this is its sharper half: a control that
    lists NOTHING, and asks the reader to recall the value instead, produces the same rejections
    with no way to see why.
  */
  const lotsHere = (existing ?? []).filter(
    (b) => b.storageFacilityId === facilityId && !HELD.includes(b.status),
  );

  /*
    A DEDUCT may only land on a lot with something to give, which is the floor read forward: a
    depleted lot holds zero, so every deduction against it breaches it. An ADD may open a new
    lot, so there the picker takes a typed number as well.

    HELD lots are excluded from both, above — the server skips them when resolving, so offering
    one would promise a correction it will not make.
  */
  const lotOptions = (
    chosenDirection === ADJUSTMENT_DIRECTION.deduct
      ? lotsHere.filter((b) => b.quantityOnHand > 0)
      : lotsHere
  ).map((b) => ({
    value: b.batchNumber,
    label: b.batchNumber,
    sublabel: `${b.quantityOnHand} ${target?.unitCode ?? ''} on hand`,
  }));

  /*
    CHANGING EITHER QUESTION ABOVE THE PICKER CLEARS IT, and this is the half a picker over a
    derived list always owes.

    `<Select>` resolves its label from its OPTIONS, so a value the list no longer holds renders
    as the placeholder — the field reads as unanswered while the state still carries the old
    number, and the form submits it. Two ways in: a lot belongs to one store, so moving the
    facility can strand it; and a lot the reader typed to OPEN is not deductible, so turning the
    direction to Deduct can too.

    The member clears when the container changes — the plantilla picker's rule, reached here by
    a second route.
  */
  function chooseFacility(next: string) {
    setFacilityId(next);
    setBatchNumber('');
  }

  function chooseDirection(next: string) {
    setDirection(next);

    // Only in the direction that NARROWS. Turning back to Add strands nothing, because every
    // lot a deduction could name is one an addition can name too.
    if (Number(next) === ADJUSTMENT_DIRECTION.deduct && !deductibleHere(batchNumber)) {
      setBatchNumber('');
    }
  }

  /** Whether a batch number names a lot at this facility with something to give. */
  function deductibleHere(number: string): boolean {
    const wanted = number.trim().toLowerCase();

    return (existing ?? []).some(
      (b) =>
        b.storageFacilityId === facilityId &&
        !HELD.includes(b.status) &&
        b.quantityOnHand > 0 &&
        b.batchNumber.trim().toLowerCase() === wanted,
    );
  }

  /*
    A LOT CANNOT GIVE UP MORE THAN IT HOLDS, mirrored from the service's own floor.

    It is NEW with the delta model: under the counted one a non-negative count could not produce
    a negative result. Checked here as well as there because the ledger's own guard THROWS, and
    an exception names no field — the form has to be able to point at the quantity.
  */
  const overdrawn = delta < 0 && batchNumber.trim() !== '' && recorded + delta < 0;


  const correctionAcknowledged = existingLot === null || ackedLotId === existingLot.id;

  /*
    A LOT IS NAMED ON AN ADD AND OPTIONAL ON A DEDUCT, which mirrors the service exactly: stock
    going ON has to land somewhere and a lot cannot be opened without a number, where stock
    coming OFF can be spread over the lots that exist.
  */
  const lotNamed = batchNumber.trim().length > 0;
  const lotRequired = chosenDirection !== ADJUSTMENT_DIRECTION.deduct;

  const valid =
    facilityId !== '' &&
    (lotNamed || !lotRequired) &&
    quantityGiven &&
    chosenDirection !== null &&
    !overdrawn &&
    (!tracksExpiry || expiryDate !== '');

  const canSave = valid && correctionAcknowledged && confirmed && !saving;

  /**
   * Why the primary action is dead, naming the FIRST thing missing in the form's own order.
   *
   * Every disabled control says why — and this one had no reason at all, which is the failure
   * that rule was written for: `<InventoryItemModal>` shipped two dead buttons and it was
   * reported as "it is not saving", with no request in the audit trail because the form never
   * became submittable.
   */
  const missing = !facilityId
    ? 'Choose the facility this stock is held at.'
    : chosenDirection === null
      ? 'Say whether this adds stock or takes it off.'
      : lotRequired && !lotNamed
        ? 'Name the lot the stock is going on — an addition has to land somewhere.'
        : !quantityGiven
          ? 'Enter how much is being added or taken off.'
          : overdrawn
            /*
              The floor, stated against the figure it was measured from. A message that only
              said "too much" would be judged against a baseline the reader cannot see — the
              defect the reason error had, and the reason the header states the lot's quantity.
            */
            ? existingLot === null
              ? `No lot "${batchNumber.trim()}" is recorded here, so there is nothing to take off.`
              // No unit: it is declared below the `target` guard, and the field's own label
              // already carries it. Repeating it here would be the same value in two slots.
              : `This lot holds ${recorded}, so at most ${recorded} can be taken off.`
            : tracksExpiry && expiryDate === ''
              ? 'This item tracks expiry, so the lot needs a date.'
              : !correctionAcknowledged
                ? 'Acknowledge that this corrects the lot that number already names.'
                : !confirmed
                  ? 'Tick the confirmation to adjust the stock.'
                  : null;

  async function save() {
    if (!canSave || !itemId) return;

    setSaving(true);
    setError(null);

    try {
      await stockBatchesApi.adjust({
        inventoryItemId: itemId,
        storageFacilityId: facilityId,
        // NULL rather than an empty string: "I do not know which lot" is the absence of an
        // answer, and the server branches on it.
        batchNumber: batchNumber.trim() || null,
        quantity: quantityNumber,
        direction: chosenDirection!,
        reason: reason.trim(),
        expiryDate: expiryDate || null,
        notes: notes.trim() || null,
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not record the adjustment.', 0);

      setError(failure);

      /*
        No `fieldIds` — every control on this form carries the id its server field derives to.
        It needed one while `direction` was a `<RadioGroup>`, which puts its id on each OPTION
        and leaves nothing at the group's own; a `<Select>` carries it on the control, so the
        derived `adjustment-direction` resolves and the mapping would be a second answer to a
        question that now has one.
      */
      focusValidationError(failure, { idPrefix: 'adjustment' });
    } finally {
      setSaving(false);
    }
  }

  if (!target) return null;

  const unit = target.unitCode;

  /*
    THERE WAS A `reasonMismatch` HERE, and it is gone with the enum — 21 Sep 2026.

    It explained why a chosen reason could not justify the direction, and it took three attempts
    to get right: the first asserted the conflict without saying why, the second measured it
    against a baseline the reader had not set. Both are worth remembering, because both are the
    shape a refusal takes when it is written from the code's point of view rather than the
    reader's — and neither is reachable now. The reason is free text, so there is nothing left
    for the direction to disagree with.
  */

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Stock Adjustment"
      /*
        LARGER than the form strictly needs, because the CALLOUT is the widest thing on it: the
        correction states recorded, counted and the resulting figure on one line, and at `lg`
        that arithmetic wrapped into three.
      */
      size="2xl"
      id="adjustment-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleAdjustment({ tracksExpiry });

          // The FACILITY is generated only where the reader has not answered — it is the first
          // question on the form, and overwriting an answer already given fights the form.
          if (facilities.length > 0 && facilityId === '') {
            setFacilityId(facilities[Math.floor(Math.random() * facilities.length)]!.id);
          }

          setBatchNumber(sample.batchNumber);
          setQuantity(sample.quantity);
          setExpiryDate(sample.expiryDate);
          setNotes(sample.notes);

          /*
            ADD, deliberately, and never a random side. It is the one direction that is valid
            against any lot — including the one this generator usually produces, which does not
            exist yet and can therefore give up nothing. A generated Deduct would breach the
            floor and leave the primary action dead, which is the single thing Autogenerate
            exists to prevent.

            It is a REQUIRED field with no default, so the generator owes it a value: leaving it
            empty would be an Autogenerate that silently stops enabling Save.
          */
          setDirection(String(ADJUSTMENT_DIRECTION.add));

          /*
            The REASON is left at its seeded suggestion, and the rule that kept it there has
            CHANGED under it rather than gone. It used to be "a recount explains a correction
            either way, so it is the one member that cannot contradict the generated
            direction" — a statement about a guard that no longer exists. Nothing can
            contradict anything now.

            What still holds is the contract: the field is required and the seed is already a
            valid answer, so Save enables on the first click. Generating prose into a free-text
            box would be inventing a reason the reader did not give, which is worse than a
            suggestion they can overtype.
          */
        },
        disabled: saving || loading,
      }}
      confirm={{
        id: 'adjustment-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="adjustment-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={canSave ? null : missing} block={false}>
            <Button
              id="adjustment-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          WHAT is being counted, stated rather than asked. The reader chose the item by choosing
          whose kebab to open, so this is a read-only fact and takes the Table Display View —
          not a picker locked to one value.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Item', value: `${target.sku} — ${target.name}`, mono: true },
            { label: 'Category', value: target.inventoryCategoryName },

            /*
              TWO figures, because the reader was being shown one and judged against the other.
              The item's total is context; the LOT's quantity is what the correction is measured
              from, and a row reading "Recorded on hand 180" directly above a form that treats
              the baseline as 0 is the form contradicting itself.

              The lot row appears only once a batch number is typed — before that there is no lot
              to state, and an em dash would read as "this lot holds nothing", which is a claim
              rather than an absence.
            */
            { label: 'Item on hand', value: `${target.onHand} ${unit}`, mono: true },
            ...(batchNumber.trim() === ''
              ? []
              : [
                  {
                    label: 'This lot',
                    value:
                      existingLot === null
                        ? 'Not on record — this opens it'
                        : `${recorded} ${unit}`,
                    mono: existingLot !== null,
                  },
                ]),

            /*
              THE LOT'S OWN COST, drawn only where a lot resolves — which is exactly when the
              figure the reader is about to type will NOT be recorded. The Unit cost field below
              says the lot's cost is not restated; this is the row that lets them see what it
              already says, so that sentence is checkable rather than a claim.

              An em dash where the lot carries no price is correct and is the one place on this
              form it is: the lot exists and nobody priced it, which is the absence
              `formatMoney` renders.
            */
            ...(existingLot === null
              ? []
              : [
                  {
                    label: "This lot's recorded cost",
                    value: formatMoney(existingLot.unitCost),
                    mono: true,
                  },
                ]),
          ]}
        />

        {/*
          FULL WIDTH, outside the grid. It is the first question and the one the rest depend on
          — the lot is resolved on item + facility + batch number, so an unanswered facility
          leaves the callout below unable to say which lot is about to be corrected. Its options
          also carry a code as a sublabel, which at half a row wraps into the control.
        */}
        <Select
          id="adjustment-storage-facility-id"
          label="Facility"
          required
          description="Where the count was taken. The same batch number at another facility is a different lot — correcting that one would move stock between buildings on paper."
          placeholder="Choose a facility"
          value={facilityId}
          onChange={chooseFacility}
          options={facilities.map((f) => ({ value: f.id, label: f.name, sublabel: f.code }))}
          remote
          loading={loading}
          disabled={saving}
          error={error?.fieldError('storageFacilityId')}
        />

        {/*
          THE THREE THAT DECIDE THE LOT, in the order the reader answers them — 29 Sep 2026.

          Quantity and direction share a row and the batch number ends the run, so what is
          being moved, which way, and onto which lot read as one block under the facility they
          all hang off. The reason sat in the middle of that run and is below it now: it is
          prose about the correction rather than part of deciding it.

          <b>The batch number alone on the second row is the END of a run, not a stranded
          field.</b> It had a grid of its own holding one control — the shape this product
          calls a section with one item in it — which is what it looked like after the unit
          cost came off this form on 21 Sep 2026 and left the row half empty.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="adjustment-quantity"
            label={`Adjusted quantity (${unit})`}
            required
            type="number"
            min={1}
            step="1"
            description="How much is being added or taken off — the CHANGE, not the new total. Taking 30 off a lot of 180 leaves 150, and this field is the 30. Zero adjusts nothing and is refused."
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={saving}
            /*
              The client-side floor takes precedence over the server's field error, which is the
              standard pairing: a message the reader can act on before sending beats one they
              meet after. `error` takes `string | undefined`, never null.
            */
            error={
              overdrawn
                ? existingLot === null
                  ? `No lot "${batchNumber.trim()}" is recorded here, so there is nothing to take off.`
                  : `This lot holds ${recorded} ${unit}. Taking off ${quantityNumber} would leave it below zero.`
                : error?.fieldError('quantity')
            }
          />

          {/*
            THE DIRECTION. Product call, 21 Sep 2026 — it shipped as a `<RadioGroup>` for one
            revision, on the argument that two opposite claims about one number should both be
            on screen, and it is a picker.

            Two cards carrying a description each ran three lines deep beside a one-line text
            field, so the row they share had one control standing three times the height of the
            other — which reads as a different KIND of field rather than a longer one, the
            failure the name row's equal columns already document. The descriptions ride as
            sublabels instead, where a picker already puts them.

            **The reason the radio was reached for survives in the SEEDING, which is the half
            that mattered.** There is no default: the placeholder says nothing about the stock,
            the primary action stays dead until a side is chosen, and `clearable` is off so it
            cannot be emptied back into a state the server would refuse.
          */}
          <Select
            id="adjustment-direction"
            label="Direction"
            required
            description="Whether the quantity is added to the lot or taken off it. This is the only thing that decides which way the stock moves — the reason beneath it is recorded as written and checked against nothing."
            placeholder="Add or deduct"
            value={direction}
            onChange={chooseDirection}
            sort="none"
            clearable={false}
            disabled={saving}
            options={Object.entries(ADJUSTMENT_DIRECTION_LABEL).map(([value, label]) => ({
              value,
              label,
              sublabel:
                ADJUSTMENT_DIRECTION_DESCRIPTION[Number(value) as StockAdjustmentDirection],
            }))}
            error={error?.fieldError('direction')}
          />

          {/*
            THE LOT, PICKED rather than recalled — and `allowCustom` only where a new one can
            legitimately be opened.

            On an ADD the reader may be recording stock under a number this facility has never
            held, so the picker takes a typed value as well. On a DEDUCT it must not: a lot that
            does not exist has nothing to give, the server refuses it, and offering free text
            there is offering a value that can only ever come back as a rejection.

            DISABLED until a facility is chosen, with the reason in its own description — a lot
            belongs to one store, so the list cannot be built before that question is answered.
            That is the container-then-member pair the plantilla picker already takes, and it is
            disabled for STATE rather than hidden: the reader is allowed to fill it in, they
            have simply not answered the question above it yet.
          */}
          <Select
            id="adjustment-batch-number"
            label="Batch number"
            required={lotRequired}
            description={
              facilityId === ''
                ? 'Choose a facility first — a lot belongs to one store, so the list is built from it.'
                : chosenDirection === ADJUSTMENT_DIRECTION.deduct
                  ? 'Which lot the stock is coming off. OPTIONAL — leave it blank and the stock is taken off the lots that exist, first to expire first. Pick one where you know it.'
                  : 'Which lot the stock is going on. Pick one this facility holds, or type a number to open a new lot. An addition has to name its lot.'
            }
            placeholder={
              chosenDirection === ADJUSTMENT_DIRECTION.deduct
                ? 'Any lot — first to expire first'
                : 'Choose a lot, or type a new number'
            }
            value={batchNumber}
            onChange={setBatchNumber}
            options={lotOptions}
            /*
              A new number is a real answer on an ADD and never on a DEDUCT. A bare
              `allowCustom` commits the typed text, which is exactly what is wanted here —
              there is no catalog behind a batch number and nothing to create.
            */
            allowCustom={chosenDirection !== ADJUSTMENT_DIRECTION.deduct}
            loading={loading}
            disabled={saving || facilityId === ''}
            error={error?.fieldError('batchNumber')}
          />

        </div>

        {/*
          THE REASON — FULL WIDTH, outside the grid, and the only free-text field on this form
          that offers suggestions.

          It is the REMARKS rule reaching a second case: a reason is the longest value here and
          the one most likely to be a sentence, so half a row is the box that truncates while
          the field beside it needed none of the room. Outside the grid rather than spanning it,
          which is the shape every other full-width field on this form already takes.

          <b>`allowCustom` with no `onCreateOption`</b>, which commits the typed text — there
          is no catalog behind a reason and nothing to create. The four it offers are what the
          enum used to enforce; they are kept because a picker that opens empty makes the reader
          invent a vocabulary one row at a time.
        */}
        <Select
          id="adjustment-reason"
          label="Reason"
          required
          description="Why the stock moved, in your own words. The suggestions are the common ones — type anything that says what happened. It is recorded on the ledger line as written."
          placeholder="Choose or type a reason"
          value={reason}
          onChange={setReason}
          sort="none"
          clearable={false}
          allowCustom
          disabled={saving}
          options={ADJUSTMENT_REASON_SUGGESTIONS.map((value) => ({ value, label: value }))}
          error={error?.fieldError('reason')}
        />


        {/*
          Drawn only where the ITEM tracks expiry, and required there. Unlike a field that
          appears as the reader works, this one is settled before the form opens — the item was
          chosen by opening the kebab — so nothing moves under them.
        */}
        {tracksExpiry ? (
          <DateField
            id="adjustment-expiry-date"
            label="Expires"
            required
            description="The day this lot lapses, read only where this count OPENS it. Correcting a lot that already exists does not restate its expiry — that was recorded when the stock arrived, and a recount does not change it."
            value={expiryDate}
            onChange={setExpiryDate}
            disabled={saving}
            error={error?.fieldError('expiryDate')}
          />
        ) : null}

        {/*
          THE CORRECTION CALLOUT — the one thing on this form that is not a field.

          An adjustment MOVES a figure somebody is relying on, which is easy to do by accident
          when batch numbers are short or reused. So the arithmetic is stated before the tick —
          what the lot holds, the change, and what it will hold after — and acknowledged against
          the LOT rather than in general: changing the number or the facility moves the
          resolution onto a different lot, and a bare boolean would carry the tick across onto
          one the reader never agreed to.

          <b>It states the RESULT, which the reader no longer types.</b> Under the counted model
          the closing figure was the number in the box; now it is arithmetic, so the callout is
          the only place they can check it before committing.
        */}
        {existingLot ? (
          <NextActionAlert
            id="adjustment-correction"
            icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
            title="This corrects an existing lot"
            description={
              <>
                <span className="font-mono">{existingLot.batchNumber}</span> at{' '}
                {existingLot.storageFacilityName} is recorded as holding{' '}
                <span className="font-mono tabular-nums">{existingLot.quantityOnHand}</span> {unit}.
                {delta !== 0 && !overdrawn ? (
                  <>
                    {' '}
                    This records a{' '}
                    <span className="font-mono font-medium tabular-nums">
                      {delta > 0 ? '+' : ''}
                      {delta}
                    </span>{' '}
                    {unit} {delta > 0 ? 'positive' : 'negative'} adjustment, leaving{' '}
                    <span className="font-mono font-medium tabular-nums">{recorded + delta}</span>{' '}
                    {unit}. The lot's own expiry and cost are NOT restated.
                  </>
                ) : (
                  <> The quantity and direction you enter move that figure up or down.</>
                )}
                <span className="mt-2 flex items-center gap-2 border-t border-amber-200 pt-2">
                  <Checkbox
                    id="adjustment-correction-ack"
                    checked={ackedLotId === existingLot.id}
                    onChange={(e) => setAckedLotId(e.target.checked ? existingLot.id : null)}
                    disabled={saving}
                  />
                  <label htmlFor="adjustment-correction-ack" className="cursor-pointer text-xs">
                    Correct the existing batch {existingLot.batchNumber}.
                  </label>
                </span>
              </>
            }
          />
        ) : null}

        {/*
          NO LOT NAMED on a deduction — the server spreads it. Said out loud before the tick,
          because it is the one path where the reader does not choose what moves and the ledger
          will carry a line per lot they never saw.
        */}
        {chosenDirection === ADJUSTMENT_DIRECTION.deduct && !lotNamed && quantityGiven ? (
          <NextActionAlert
            id="adjustment-allocated"
            icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
            title="This is spread across lots"
            description={
              <>
                No lot is named, so{' '}
                <span className="font-mono font-medium tabular-nums">{quantityNumber}</span> {unit}{' '}
                comes off whichever lots this facility holds, <b>first to expire first</b> — one
                ledger line each, naming its own lot. The attribution is a convention rather than
                a finding: the stock is not there, so no lot is more at fault than another. Name
                one above if you know which.
              </>
            }
          />
        ) : null}

        {/*
          NO LOT RESOLVES, so an ADD brings one into existence — the Found-item case. Said out
          loud because it is the one path where nothing is being moved and the reader should not
          be looking for a lot that is not there.

          A DEDUCT down this path is refused rather than explained, by the floor check — so this
          callout is deliberately not the place that says so. Two surfaces describing one
          impossible state is how they end up describing it differently.
        */}
        {!existingLot && held.length === 0 && elsewhere.length === 0 && batchNumber.trim() && delta > 0 ? (
          <NextActionAlert
            id="adjustment-new-lot"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
            title="This creates a lot"
            description={
              <>
                Nothing is recorded under <span className="font-mono">{batchNumber.trim()}</span>{' '}
                here, so this opens it holding{' '}
                <span className="font-mono font-medium tabular-nums">{quantityNumber}</span> {unit}
                {' '}as a positive adjustment. Nothing is recorded as RECEIVED — the stock was
                found, not delivered.
              </>
            }
          />
        ) : null}

        {/*
          The same number exists here and CANNOT be topped up. Saying so is what stops the
          reader assuming they are adding to the lot they know about — held stock is never
          topped up, so what they get is a separate issuable lot.
        */}
        {!existingLot && held.length > 0 ? (
          <NextActionAlert
            id="adjustment-held"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
            title="That batch is on hold here"
            description={
              <>
                <span className="font-mono">{batchNumber.trim()}</span> exists at this facility
                and is{' '}
                {held.map((b) => BATCH_STATUS_LABEL[b.status].toLowerCase()).join(' / ')} — held
                stock is never topped up, so a separate issuable lot will be created.
              </>
            }
          />
        ) : null}

        {!existingLot && held.length === 0 && elsewhere.length > 0 ? (
          <NextActionAlert
            id="adjustment-elsewhere"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
            title="That batch is at another facility"
            description={
              <>
                <span className="font-mono">{batchNumber.trim()}</span> exists at{' '}
                {[...new Set(elsewhere.map((b) => b.storageFacilityName))].join(', ')}, not at the
                facility chosen above — a separate lot will be created here.
              </>
            }
          />
        ) : null}

        {/* FULL WIDTH and outside the grid: a remark is the longest value on the form and the
            one most likely to be a sentence. */}
        <TextField
          id="adjustment-notes"
          label="Notes"
          description="Anything worth knowing about this count — where the discrepancy came from, what was recounted, who else verified it."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('notes')}
        />
      </div>
    </Modal>
  );
}
